import { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ShieldCheck, Home, FileText, FileSignature, Camera, Wallet, Plus, RefreshCw, Search, X, Eye,
  Share2, ThumbsUp, ThumbsDown, Archive, ArchiveRestore, Copy, ExternalLink, Send, QrCode,
  AlertTriangle, CheckCircle2, Clock, Car, Settings, History,
} from 'lucide-react'
import QRCode from 'qrcode'
import api from '../api/client'
import Loader from '../components/Loader'
import Pagination from '../components/Pagination'
import SearchableSelect from '../components/SearchableSelect'
import { successModal, errorModal, confirmModal } from '../components/ui'

// Proteção Veicular dentro do EV — fluxo da operação oficial (LabelView).
// Dados reais filtrados pelo consultor do licenciado. Ações de negociação são REGISTRO LOCAL
// provisório ("não sincronizado") até a LabelView liberar o endpoint de status; o oficial prevalece.
// Comissão só nasce da fatura PAGA (adesão e cada mensalidade) e cai na carteira do EV.

const money = (v) => (v == null || v === '' ? '-' : 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
const fmt = (v) => (v ? new Date(v).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-')
const fmtD = (v) => {
  if (!v) return '-'
  const s = String(v)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [a, m, d] = s.split('-'); return `${d}/${m}/${a}` }
  return new Date(v).toLocaleDateString('pt-BR')
}
const card = 'bg-white rounded-2xl border border-neutral-200 shadow-sm'
const btnSec = 'inline-flex items-center gap-1.5 rounded-xl border border-neutral-200 text-neutral-700 px-3 py-2 text-sm font-semibold hover:bg-neutral-50 disabled:opacity-50'
const btnPri = 'inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-primary-500 to-accent-500 text-white px-4 py-2 text-sm font-bold shadow-md disabled:opacity-50'
const erroDe = (e, pad) => e?.response?.data?.detail || pad

const ST_COT = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  sent: 'bg-sky-100 text-sky-800 border-sky-300',
  converted: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  lost: 'bg-neutral-200 text-neutral-600 border-neutral-300',
  expired: 'bg-neutral-200 text-neutral-600 border-neutral-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
}
const Pill = ({ cls, children }) => <span className={`inline-block px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${cls || 'bg-neutral-100 text-neutral-700 border-neutral-200'}`}>{children}</span>

function StatusCotacao({ q }) {
  return (
    <div className="space-y-1">
      <Pill cls={ST_COT[q.status]}>{q.status_label || q.status}</Pill>
      {q.status_local && (
        <p className="text-[10px] leading-tight text-violet-700 font-semibold" title="Registro feito no EV. A operação oficial ainda não expõe a alteração de status; quando expuser, o status oficial prevalece.">
          {q.status_local_label} · registro local, não sincronizado
        </p>
      )}
    </div>
  )
}

function Vazio({ children }) {
  return <p className="px-5 py-8 text-sm text-neutral-400 text-center">{children}</p>
}

function Modal({ titulo, onClose, children, largo }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3" onClick={onClose}>
      <div className={`${card} w-full ${largo ? 'max-w-5xl' : 'max-w-2xl'} max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 sticky top-0 bg-white z-10">
          <p className="font-heading font-bold text-graphite">{titulo}</p>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-700" aria-label="Fechar"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function QrLink({ url, size = 180 }) {
  const [src, setSrc] = useState('')
  useEffect(() => {
    if (!url) return
    QRCode.toDataURL(url, { width: size, margin: 1 }).then(setSrc).catch(() => setSrc(''))
  }, [url, size])
  if (!src) return null
  return (
    <div className="flex flex-col items-center gap-2">
      <img src={src} alt="QR Code do link de cotação" width={size} height={size} className="rounded-lg border border-neutral-200" />
      <a href={src} download="qrcode-cotacao.png" className="text-xs font-semibold text-accent-700 hover:underline">Baixar QR Code</a>
    </div>
  )
}

const abrirWhats = (tel, texto) => {
  const n = String(tel || '').replace(/\D/g, '')
  const num = n ? (n.length <= 11 ? '55' + n : n) : ''
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(texto)}`, '_blank')
}

/* ═════════ Nova Cotação: formulário oficial dentro do EV ═════════ */
function NovaCotacao({ link, onClose }) {
  const [tel, setTel] = useState('')
  const [qr, setQr] = useState(false)
  const texto = `Olá! Faça sua cotação de Proteção Veicular pelo link abaixo, é rápido e você já vê o valor da mensalidade:\n${link}`
  const copiar = () => { try { navigator.clipboard.writeText(link); successModal('Link copiado!') } catch { /* */ } }
  return (
    <Modal titulo="Nova Cotação — formulário oficial" onClose={onClose} largo>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={copiar} className={btnSec}><Copy size={15} /> Copiar link</button>
        <a href={link} target="_blank" rel="noreferrer" className={btnSec}><ExternalLink size={15} /> Abrir em nova aba</a>
        <button onClick={() => setQr((v) => !v)} className={btnSec}><QrCode size={15} /> QR Code</button>
        <input value={tel} onChange={(e) => setTel(e.target.value)} placeholder="WhatsApp do cliente (opcional)"
          className="flex-1 min-w-[170px] border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-accent-400" />
        <button onClick={() => abrirWhats(tel, texto)} className={btnPri}><Send size={15} /> Enviar ao cliente</button>
      </div>
      {qr && <div className="mb-3 flex justify-center"><QrLink url={link} /></div>}
      <p className="text-xs text-neutral-500 mb-2">Preencha aqui com o cliente ou envie o link. A cotação é criada na operação oficial e já fica vinculada a você.</p>
      <iframe title="Formulário oficial de cotação" src={link} className="w-full h-[70vh] rounded-xl border border-neutral-200 bg-white" />
    </Modal>
  )
}

/* ═════════ Início ═════════ */
function Inicio({ params, irPara, abrirNova, resumo, recarregar }) {
  if (!resumo) return <Loader />
  if (resumo.erro) return <div className={`${card} p-5 text-sm text-red-700`}>{resumo.erro}</div>
  if (!resumo.vinculado) return <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">{resumo.motivo}</div>
  const kpis = [
    ['Leads', resumo.abas?.leads, FileText, () => irPara('cotacoes', 'leads')],
    ['Em negociação', resumo.em_negociacao, Clock, () => irPara('cotacoes', 'leads')],
    ['Adesões', resumo.abas?.adesoes, CheckCircle2, () => irPara('cotacoes', 'adesoes')],
    ['Contratos ativos', resumo.contratos_ativos, FileSignature, () => irPara('contratos')],
    ['Vistorias pendentes', resumo.vistorias_pendentes, Camera, () => irPara('vistorias')],
    ['Ganhos (carteira)', money(resumo.ganhos_total), Wallet, () => irPara('ganhos')],
  ]
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map(([l, v, Icon, go]) => (
          <button key={l} onClick={go} className={`${card} p-4 text-left hover:border-accent-300 transition`}>
            <div className="flex items-center gap-2 text-xs font-semibold text-neutral-500"><Icon size={15} className="text-accent-500" /> {l}</div>
            <p className="mt-1 text-2xl font-heading font-extrabold text-graphite">{v ?? 0}</p>
          </button>
        ))}
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className={`${card} p-5 lg:col-span-2 space-y-3`}>
          <p className="font-heading font-bold text-graphite">Seu link de cotação</p>
          <p className="text-xs text-neutral-500">O cliente informa o veículo, vê o valor real da mensalidade e a cotação já fica no seu nome.</p>
          <p className="text-sm bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2 break-all select-all font-medium text-graphite">{resumo.link}</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={abrirNova} className={btnPri}><Plus size={15} /> Nova Cotação</button>
            <button onClick={() => { try { navigator.clipboard.writeText(resumo.link); successModal('Link copiado!') } catch { /* */ } }} className={btnSec}><Copy size={15} /> Copiar link</button>
            <button onClick={() => abrirWhats('', `Olá! Faça sua cotação de Proteção Veicular pelo link abaixo:\n${resumo.link}`)} className={btnSec}><Send size={15} /> WhatsApp</button>
          </div>
        </div>
        <div className={`${card} p-5 flex flex-col items-center justify-center`}>
          <p className="text-xs font-semibold text-neutral-500 mb-2">QR Code do seu link</p>
          <QrLink url={resumo.link} size={160} />
        </div>
      </div>
      <div className={card}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100">
          <p className="font-heading font-bold text-graphite">Cotações recentes</p>
          <button onClick={recarregar} className="inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:underline"><RefreshCw size={13} /> Atualizar</button>
        </div>
        {(resumo.recentes || []).length === 0 ? <Vazio>Nenhuma cotação ainda. Envie seu link aos clientes.</Vazio> : (
          <div className="divide-y divide-neutral-100">
            {resumo.recentes.map((q) => (
              <div key={q.externo_id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div><p className="font-semibold text-graphite">{q.cliente || '-'}</p><p className="text-[11px] text-neutral-400">{q.veiculo} {q.placa ? `· ${q.placa}` : ''}</p></div>
                <div className="flex items-center gap-3"><span className="font-semibold text-accent-700">{money(q.valor_mensal)}</span><StatusCotacao q={q} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/* ═════════ Cotações ═════════ */
function textoCotacao(q, link) {
  return `Olá${q.cliente ? ', ' + q.cliente.split(' ')[0] : ''}! Segue sua cotação de Proteção Veicular:\n`
    + `Veículo: ${q.veiculo || '-'}${q.placa ? ' (' + q.placa + ')' : ''}\n`
    + `Mensalidade: ${money(q.valor_mensal)}\nAdesão: ${money(q.adesao)}\n`
    + (q.fipe ? `Valor FIPE: ${money(q.fipe)}\n` : '')
    + (q.valida_ate ? `Válida até: ${fmtD(q.valida_ate)}\n` : '')
    + (link ? `\nPara contratar ou refazer a cotação: ${link}` : '')
}

function useAcoesCotacao(params, onDone) {
  const [busy, setBusy] = useState(false)
  const registrar = async (q, status) => {
    const rot = { ganha: 'negociação GANHA', perdida: 'negociação PERDIDA', arquivada: 'lead ARQUIVADO', ativa: 'lead RECUPERADO' }[status]
    const ok = await confirmModal(
      `Registrar ${rot} para ${q.cliente || 'esta cotação'}? Este é um registro local do EV (não sincronizado com a operação oficial). Não gera comissão: a comissão só nasce quando a adesão é paga.`,
      { title: 'Registrar negociação', okLabel: 'Registrar' })
    if (!ok) return
    const nota = (status === 'perdida' || status === 'arquivada') ? (window.prompt('Motivo / observação (opcional):') || null) : null
    setBusy(true)
    try {
      await api.post(`/protecao-veicular/lv/cotacoes/${q.externo_id}/status`, { status, nota }, { params })
      onDone && onDone()
    } catch (e) { errorModal(erroDe(e, 'Não foi possível registrar.')) } finally { setBusy(false) }
  }
  return { busy, registrar }
}

function AcoesCotacao({ q, link, params, onDone, onAbrir, compacto }) {
  const { busy, registrar } = useAcoesCotacao(params, onDone)
  const b = compacto ? 'p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40' : btnSec
  const arquivado = q.aba === 'arquivados'
  const adesao = q.aba === 'adesoes'
  return (
    <div className="flex flex-wrap gap-1.5">
      {onAbrir && <button title="Abrir" onClick={() => onAbrir(q)} className={b}><Eye size={15} />{!compacto && ' Abrir'}</button>}
      <button title="Compartilhar" onClick={() => abrirWhats(q.telefone, textoCotacao(q, link))} className={b}><Share2 size={15} />{!compacto && ' Compartilhar'}</button>
      {!adesao && !arquivado && <>
        <button title="Negociação ganha" disabled={busy} onClick={() => registrar(q, 'ganha')} className={`${b} text-emerald-700`}><ThumbsUp size={15} />{!compacto && ' Ganha'}</button>
        <button title="Negociação perdida" disabled={busy} onClick={() => registrar(q, 'perdida')} className={`${b} text-red-700`}><ThumbsDown size={15} />{!compacto && ' Perdida'}</button>
        <button title="Arquivar" disabled={busy} onClick={() => registrar(q, 'arquivada')} className={b}><Archive size={15} />{!compacto && ' Arquivar'}</button>
      </>}
      {arquivado && <button title="Recuperar lead" disabled={busy} onClick={() => registrar(q, 'ativa')} className={`${b} text-sky-700`}><ArchiveRestore size={15} />{!compacto && ' Recuperar'}</button>}
    </div>
  )
}

function CotacaoModal({ id, link, params, onClose, onDone }) {
  const [d, setD] = useState(null)
  const load = useCallback(() => {
    api.get(`/protecao-veicular/lv/cotacoes/${id}`, { params }).then((r) => setD(r.data))
      .catch((e) => setD({ erro: erroDe(e, 'Não foi possível abrir a cotação.') }))
  }, [id, params])
  useEffect(() => { load() }, [load])
  const campo = (l, v) => <div><p className="text-[11px] uppercase font-semibold text-neutral-400">{l}</p><p className="text-sm text-graphite font-medium">{v || '-'}</p></div>
  return (
    <Modal titulo="Cotação" onClose={onClose}>
      {!d ? <Loader /> : d.erro ? <p className="text-sm text-red-700">{d.erro}</p> : (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-2">
            <div><p className="font-heading font-bold text-lg text-graphite">{d.cliente || '-'}</p><p className="text-xs text-neutral-500">{d.telefone} {d.email ? `· ${d.email}` : ''}</p></div>
            <StatusCotacao q={d} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {campo('Veículo', d.veiculo)}{campo('Placa', d.placa)}{campo('Valor FIPE', money(d.fipe))}
            {campo('Mensalidade', money(d.valor_mensal))}{campo('Adesão', money(d.adesao))}{campo('Criada em', fmt(d.criada_em))}
            {campo('Válida até', fmtD(d.valida_ate))}{campo('Convertida em', d.convertida_em ? fmt(d.convertida_em) : '-')}
          </div>
          <AcoesCotacao q={d} link={link} params={params} onDone={() => { load(); onDone && onDone() }} />
          <div>
            <p className="text-xs font-bold text-neutral-500 uppercase mb-1 flex items-center gap-1"><History size={13} /> Histórico da negociação</p>
            <ul className="text-sm space-y-1">
              {d.convertida_em && <li className="text-emerald-700">{fmt(d.convertida_em)} — Adesão realizada (operação oficial)</li>}
              {(d.historico_local || []).map((h, i) => (
                <li key={i} className="text-neutral-700">{fmt(h.em)} — {h.label} <span className="text-[11px] text-violet-700">(registro local)</span>{h.nota ? ` · ${h.nota}` : ''}</li>
              ))}
              <li className="text-neutral-500">{fmt(d.criada_em)} — Cotação criada (operação oficial)</li>
            </ul>
          </div>
        </div>
      )}
    </Modal>
  )
}

function Cotacoes({ params, link, abaInicial, abrirNova, fresco }) {
  const [sub, setSub] = useState(abaInicial || 'leads')
  const [d, setD] = useState(null)
  const [busca, setBusca] = useState('')
  const [aberta, setAberta] = useState(null)
  const load = useCallback((atualizar) => {
    setD(null)
    api.get('/protecao-veicular/lv/cotacoes', { params: { ...params, ...(atualizar ? { atualizar: 1 } : {}) } })
      .then((r) => setD(r.data)).catch((e) => setD({ erro: erroDe(e, 'Não foi possível buscar as cotações.') }))
  }, [params])
  useEffect(() => { load(fresco) }, [load, fresco])
  useEffect(() => { if (abaInicial) setSub(abaInicial) }, [abaInicial])

  const todas = d?.cotacoes || []
  const t = busca.trim().toLowerCase()
  const rows = todas.filter((q) => q.aba === sub && (!t || [q.cliente, q.placa, q.veiculo, q.telefone].some((x) => String(x || '').toLowerCase().includes(t))))
  const emNeg = todas.filter((q) => q.aba === 'leads' && ['pending', 'sent'].includes(q.status)).length
  const SUBS = [['leads', 'Leads'], ['arquivados', 'Arquivados'], ['adesoes', 'Adesões']]
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 font-semibold">{d?.abas?.leads ?? 0} leads</span>
          <span className="px-3 py-1.5 rounded-full bg-sky-50 border border-sky-200 text-sky-800 font-semibold">{emNeg} em negociação</span>
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold">{d?.abas?.adesoes ?? 0} adesões</span>
        </div>
        <button onClick={abrirNova} className={btnPri}><Plus size={15} /> Nova Cotação</button>
      </div>
      <div className={card}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 pt-3 border-b border-neutral-100">
          <div className="flex gap-1">
            {SUBS.map(([k, l]) => (
              <button key={k} onClick={() => setSub(k)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${sub === k ? 'border-accent-500 text-accent-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>
                {l} <span className="text-xs text-neutral-400">({d?.abas?.[k] ?? 0})</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 pb-2">
            <div className="relative"><Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
              <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Cliente, placa, telefone"
                className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-sm outline-none focus:border-accent-400 w-48" /></div>
            <button onClick={() => load(true)} className="inline-flex items-center gap-1 text-xs font-semibold text-accent-700 hover:underline"><RefreshCw size={13} /> Atualizar</button>
          </div>
        </div>
        {!d ? <Loader /> : d.erro ? <p className="px-5 py-6 text-sm text-red-700">{d.erro}</p> : rows.length === 0 ? <Vazio>Nada por aqui.</Vazio> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gradient-to-r from-primary-50 to-accent-50 text-xs uppercase text-graphite">
                {['Cliente', 'Veículo / Placa', 'Mensalidade', 'FIPE', 'Data', 'Status', 'Ações'].map((h) => <th key={h} className="px-4 py-2 text-left font-bold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((q) => (
                  <tr key={q.externo_id} className="border-t border-neutral-100 align-top">
                    <td className="px-4 py-2 min-w-[140px]"><p className="font-semibold text-graphite">{q.cliente || '-'}</p><p className="text-[11px] text-neutral-400 whitespace-nowrap">{q.telefone || ''}</p></td>
                    <td className="px-4 py-2 min-w-[180px]"><p className="text-neutral-700">{q.veiculo || '-'}</p><p className="text-[11px] text-neutral-400">{q.placa || ''}</p></td>
                    <td className="px-4 py-2 font-semibold text-accent-700 whitespace-nowrap">{money(q.valor_mensal)}<p className="text-[11px] font-normal text-neutral-400">adesão {money(q.adesao)}</p></td>
                    <td className="px-4 py-2 text-neutral-600 whitespace-nowrap">{money(q.fipe)}</td>
                    <td className="px-4 py-2 text-xs text-neutral-500 whitespace-nowrap">{fmt(q.criada_em)}</td>
                    <td className="px-4 py-2"><StatusCotacao q={q} /></td>
                    <td className="px-4 py-2"><AcoesCotacao q={q} link={link} params={params} onDone={() => load(true)} onAbrir={(x) => setAberta(x.externo_id)} compacto /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <p className="text-[11px] text-neutral-400">Ganha / Perdida / Arquivar / Recuperar ficam registrados no EV como status local provisório, até a operação oficial liberar a sincronização. Status oficial sempre prevalece.</p>
      {aberta && <CotacaoModal id={aberta} link={link} params={params} onClose={() => setAberta(null)} onDone={() => load(true)} />}
    </div>
  )
}

/* ═════════ Contratos ═════════ */
function ContratoModal({ c, onClose }) {
  return (
    <Modal titulo={`Contrato ${c.numero || '(em andamento)'}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="grid grid-cols-2 gap-3">
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Cliente</p><p className="font-medium text-graphite">{c.cliente || '-'}</p></div>
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Veículo</p><p className="font-medium text-graphite">{c.veiculo} · {c.placa}</p></div>
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Proposta</p><p className="font-medium">{c.situacao_proposta}</p></div>
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Ativação</p><p className="font-medium">{c.situacao_ativacao}</p></div>
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Vigência</p><p className="font-medium">{fmtD(c.vigencia_inicio)}{c.vigencia_fim ? ` até ${fmtD(c.vigencia_fim)}` : ''}</p></div>
          <div><p className="text-[11px] uppercase font-semibold text-neutral-400">Mensalidade</p><p className="font-medium">{money(c.valor_mensal)}</p></div>
        </div>
        {c.pendencias.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs font-bold text-amber-800 mb-1">Pendências</p>
            <ul className="list-disc pl-5 text-amber-900">{c.pendencias.map((p, i) => <li key={i}>{p}</li>)}</ul>
          </div>
        )}
        {c.faturas.length > 0 && (
          <div>
            <p className="text-xs font-bold text-neutral-500 uppercase mb-1">Faturas</p>
            <table className="w-full text-xs">
              <thead><tr className="text-neutral-400 text-left"><th className="py-1">Fatura</th><th>Tipo</th><th>Vencimento</th><th>Valor</th><th>Situação</th></tr></thead>
              <tbody>{c.faturas.map((f) => (
                <tr key={f.numero} className="border-t border-neutral-100"><td className="py-1">{f.numero}</td><td>{f.tipo}{f.mes ? ` ${f.mes}` : ''}</td><td>{fmtD(f.vencimento)}</td><td>{money(f.valor)}</td><td>{f.situacao}</td></tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <div>
          <p className="text-xs font-bold text-neutral-500 uppercase mb-1 flex items-center gap-1"><History size={13} /> Histórico de atualizações</p>
          <ul className="space-y-1">{c.historico.map((h, i) => <li key={i} className="text-neutral-700">{fmt(h.data)} — {h.evento}</li>)}</ul>
        </div>
      </div>
    </Modal>
  )
}

function Contratos({ params }) {
  const [rows, setRows] = useState(null)
  const [erro, setErro] = useState('')
  const [aberto, setAberto] = useState(null)
  useEffect(() => {
    setRows(null); setErro('')
    api.get('/protecao-veicular/lv/contratos', { params }).then((r) => setRows(r.data.contratos || []))
      .catch((e) => { setRows([]); setErro(erroDe(e, 'Não foi possível buscar os contratos.')) })
  }, [params])
  return (
    <div className={card}>
      {rows === null ? <Loader /> : erro ? <p className="px-5 py-6 text-sm text-red-700">{erro}</p> : rows.length === 0 ? <Vazio>Nenhum contrato ainda. Os contratos aparecem aqui quando a cotação vira adesão na operação.</Vazio> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-gradient-to-r from-primary-50 to-accent-50 text-xs uppercase text-graphite">
              {['Cliente / Veículo', 'Contrato', 'Contratação', 'Proposta', 'Ativação', 'Vigência', 'Pendências', ''].map((h) => <th key={h} className="px-4 py-2 text-left font-bold">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.veiculo_id} className="border-t border-neutral-100 align-top">
                  <td className="px-4 py-2"><p className="font-semibold text-graphite">{c.cliente || '-'}</p><p className="text-[11px] text-neutral-400">{c.veiculo} · {c.placa}</p></td>
                  <td className="px-4 py-2 text-xs font-mono">{c.numero || '-'}</td>
                  <td className="px-4 py-2 text-xs text-neutral-500">{fmtD(c.data_contratacao)}</td>
                  <td className="px-4 py-2 text-xs">{c.situacao_proposta}</td>
                  <td className="px-4 py-2"><Pill cls={c.ativo ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>{c.situacao_ativacao}</Pill></td>
                  <td className="px-4 py-2 text-xs">{c.vigencia_inicio ? fmtD(c.vigencia_inicio) : '-'}{c.vigencia_fim ? ` a ${fmtD(c.vigencia_fim)}` : ''}</td>
                  <td className="px-4 py-2 text-xs">{c.pendencias.length ? <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><AlertTriangle size={13} /> {c.pendencias.length}</span> : <span className="text-emerald-700">Nenhuma</span>}</td>
                  <td className="px-4 py-2"><button onClick={() => setAberto(c)} className="p-1.5 rounded-lg border border-neutral-200 hover:bg-neutral-50" title="Detalhes"><Eye size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {aberto && <ContratoModal c={aberto} onClose={() => setAberto(null)} />}
    </div>
  )
}

/* ═════════ Vistorias ═════════ */
function Vistorias({ params }) {
  const [rows, setRows] = useState(null)
  const [erro, setErro] = useState('')
  useEffect(() => {
    setRows(null); setErro('')
    api.get('/protecao-veicular/lv/vistorias', { params }).then((r) => setRows(r.data.vistorias || []))
      .catch((e) => { setRows([]); setErro(erroDe(e, 'Não foi possível buscar as vistorias.')) })
  }, [params])
  const cls = (s) => (s === 'approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : s === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300')
  if (rows === null) return <Loader />
  if (erro) return <div className={`${card} p-5 text-sm text-red-700`}>{erro}</div>
  if (rows.length === 0) return <div className={card}><Vazio>Nenhuma vistoria dos seus veículos até agora.</Vazio></div>
  return (
    <div className="grid md:grid-cols-2 gap-3">
      {rows.map((v) => (
        <div key={v.id} className={`${card} p-4 space-y-2 text-sm`}>
          <div className="flex items-start justify-between gap-2">
            <div><p className="font-semibold text-graphite">{v.cliente || '-'}</p><p className="text-[11px] text-neutral-400">{v.veiculo} · {v.placa} · {v.codigo}{v.renovacao ? ' · renovação' : ''}</p></div>
            <Pill cls={cls(v.status)}>{v.situacao}</Pill>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><p className="text-neutral-400">Solicitada</p><p>{fmtD(v.criada_em)}</p></div>
            <div><p className="text-neutral-400">Agendada</p><p>{fmtD(v.agendada_para)}</p></div>
            <div><p className="text-neutral-400">Realizada</p><p>{fmtD(v.realizada_em)}</p></div>
          </div>
          <p className="text-xs"><span className="text-neutral-400">Resultado: </span><span className="font-semibold">{v.resultado}</span></p>
          {v.pendencias.length > 0 && <p className="text-xs text-amber-800 flex items-center gap-1"><AlertTriangle size={12} /> {v.pendencias.join(' · ')}</p>}
          <p className="text-xs bg-neutral-50 border border-neutral-100 rounded-lg px-3 py-2 text-neutral-700">{v.orientacao}</p>
          {v.link && v.status !== 'approved' && (
            <div className="flex flex-wrap gap-2">
              <button onClick={() => { try { navigator.clipboard.writeText(v.link); successModal('Link da vistoria copiado!') } catch { /* */ } }} className={btnSec}><Copy size={14} /> Copiar link da vistoria</button>
              <button onClick={() => abrirWhats('', `Olá! Segue o link para fazer a vistoria do seu veículo (${v.placa}):\n${v.link}`)} className={btnSec}><Send size={14} /> Enviar</button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/* ═════════ Ganhos (carteira do EV) ═════════ */
function ProcessarComissoes() {
  const [res, setRes] = useState(null)
  const [busy, setBusy] = useState(false)
  const [desde, setDesde] = useState('2026-10-20')
  const rodar = async (dry) => {
    if (!dry && !(await confirmModal(`Creditar as comissões das faturas pagas desde ${fmtD(desde)}? A partir disso o crédito passa a ser automático (a cada 10 minutos).`, { title: 'Processar comissões', okLabel: 'Creditar' }))) return
    setBusy(true)
    try { const r = await api.post('/protecao-veicular/lv/sincronizar-comissoes', null, { params: { dry_run: dry, desde } }); setRes(r.data) }
    catch (e) { errorModal(erroDe(e, 'Falha ao processar.')) } finally { setBusy(false) }
  }
  return (
    <div className={`${card} p-4 space-y-2`}>
      <p className="font-heading font-bold text-graphite flex items-center gap-2"><Settings size={16} className="text-accent-500" /> Comissões por fatura paga (admin)</p>
      <p className="text-xs text-neutral-500">Adesão paga gera a comissão de adesão; cada mensalidade paga gera a recorrência. Cada fatura é processada uma única vez.</p>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-neutral-600">Pagas desde</label>
        <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
        <button disabled={busy} onClick={() => rodar(true)} className={btnSec}>Simular</button>
        <button disabled={busy} onClick={() => rodar(false)} className={btnPri}>Creditar</button>
      </div>
      {res && (
        <div className="text-xs space-y-1">
          <p>{res.dry_run ? 'Simulação' : 'Processado'}: {res.verificadas} fatura(s) nova(s) · creditadas {res.creditadas} · sem elegibilidade {res.sem_elegibilidade}{res.automatico_desde ? ` · automático desde ${fmtD(res.automatico_desde)}` : ' · automático desligado'}</p>
          {(res.itens || []).map((i, k) => <p key={k} className="text-neutral-600">{i.fatura} · {i.licenciado} · {i.placa} · {i.tipo} · {money(i.valor)}{i.comissao != null ? ` → ${money(i.comissao)}` : ''}{i.elegivel === false ? ' (licença não ativa)' : ''}</p>)}
        </div>
      )}
    </div>
  )
}

function Ganhos({ params, isAdmin }) {
  const nav = useNavigate()
  const [page, setPage] = useState(1)
  const [d, setD] = useState(null)
  useEffect(() => {
    setD(null)
    api.get('/protecao-veicular/lv/ganhos', { params: { ...params, page, limit: 20 } }).then((r) => setD(r.data))
      .catch((e) => setD({ erro: erroDe(e, 'Não foi possível carregar os ganhos.') }))
  }, [params, page])
  if (!d) return <Loader />
  if (d.erro) return <div className={`${card} p-5 text-sm text-red-700`}>{d.erro}</div>
  const kpi = [['Saldo da carteira', d.saldo_carteira], ['Ganhos Proteção Veicular', d.total], ['Adesões', d.total_adesao], ['Recorrência', d.total_recorrencia]]
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpi.map(([l, v]) => <div key={l} className={`${card} p-4`}><p className="text-xs font-semibold text-neutral-500">{l}</p><p className="mt-1 text-xl font-heading font-extrabold text-graphite">{money(v)}</p></div>)}
      </div>
      {!isAdmin && <button onClick={() => nav('/financeiro/carteira')} className={btnSec}><Wallet size={15} /> Ir para Minha Carteira</button>}
      {isAdmin && <ProcessarComissoes />}
      <div className={card}>
        <p className="px-5 py-3 border-b border-neutral-100 font-heading font-bold text-graphite">Lançamentos na carteira</p>
        {d.itens.length === 0 ? <Vazio>Nenhum ganho de Proteção Veicular ainda. A comissão é lançada quando a adesão ou a mensalidade do cliente é paga.</Vazio> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-gradient-to-r from-primary-50 to-accent-50 text-xs uppercase text-graphite">
                {['Data', 'Descrição', 'Tipo', 'Origem', 'PV', 'Valor'].map((h) => <th key={h} className="px-4 py-2 text-left font-bold">{h}</th>)}
              </tr></thead>
              <tbody>{d.itens.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100">
                  <td className="px-4 py-2 text-xs text-neutral-500">{fmt(c.data)}</td>
                  <td className="px-4 py-2 text-neutral-700">{c.descricao}</td>
                  <td className="px-4 py-2 text-xs">{c.tipo}</td>
                  <td className="px-4 py-2 text-xs">{c.origem}</td>
                  <td className="px-4 py-2 text-xs">{c.pv || '-'}</td>
                  <td className={`px-4 py-2 font-semibold ${c.status === 'estornado' ? 'text-neutral-400 line-through' : 'text-emerald-700'}`}>{money(c.valor)}</td>
                </tr>
              ))}</tbody>
            </table>
            <Pagination page={d.page} pages={d.pages} total={d.count} pageSize={20} onPage={setPage} />
          </div>
        )}
      </div>
      {d.faturas_processadas.length > 0 && (
        <div className={card}>
          <p className="px-5 py-3 border-b border-neutral-100 font-heading font-bold text-graphite">Faturas pagas dos seus clientes</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase text-neutral-500">{['Pago em', 'Fatura', 'Placa', 'Tipo', 'Valor', 'PV', 'Comissão', 'Situação'].map((h) => <th key={h} className="px-4 py-2 text-left">{h}</th>)}</tr></thead>
              <tbody>{d.faturas_processadas.map((f) => (
                <tr key={f.fatura} className="border-t border-neutral-100">
                  <td className="px-4 py-2 text-xs">{fmt(f.pago_em)}</td><td className="px-4 py-2 text-xs font-mono">{f.fatura}</td>
                  <td className="px-4 py-2 text-xs">{f.placa}</td><td className="px-4 py-2 text-xs">{f.tipo}{f.mes ? ` ${f.mes}` : ''}</td>
                  <td className="px-4 py-2 text-xs">{money(f.valor_base)}</td><td className="px-4 py-2 text-xs">{f.pv}</td>
                  <td className="px-4 py-2 text-xs font-semibold">{money(f.comissao)}</td>
                  <td className="px-4 py-2 text-xs">{f.situacao === 'creditada' ? 'Creditada' : `Sem comissão (${f.motivo || '-'})`}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

/* ═════════ Página ═════════ */
const ABAS = [
  ['inicio', 'Início', Home], ['cotacoes', 'Cotações', FileText], ['contratos', 'Contratos', FileSignature],
  ['vistorias', 'Vistorias', Camera], ['ganhos', 'Ganhos', Wallet],
]

export default function ProtecaoVeicular({ novaAoAbrir = false }) {
  const nav = useNavigate()
  const [sp, setSp] = useSearchParams()
  const isAdmin = (JSON.parse(localStorage.getItem('user') || '{}').perfil || '') === 'admin'
  const aba = ABAS.some(([k]) => k === sp.get('aba')) ? sp.get('aba') : 'inicio'
  const sub = sp.get('sub') || ''
  const [lics, setLics] = useState([])
  const [licId, setLicId] = useState(sp.get('licenciado') || '')
  const [resumo, setResumo] = useState(null)
  const [nova, setNova] = useState(false)
  const [rev, setRev] = useState(0)
  const params = isAdmin ? (licId ? { licenciado_id: Number(licId) } : null) : {}
  const paramsKey = JSON.stringify(params)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const p = useMemo(() => params, [paramsKey])

  useEffect(() => { if (isAdmin) api.get('/licenciados?limit=1000').then((r) => setLics(r.data.data || [])).catch(() => {}) }, [isAdmin])
  const carregarResumo = useCallback(() => {
    if (!p) return
    setResumo(null)
    api.get('/protecao-veicular/lv/resumo', { params: p }).then((r) => setResumo(r.data))
      .catch((e) => setResumo({ erro: erroDe(e, 'Não foi possível carregar a Proteção Veicular.') }))
  }, [p])
  useEffect(() => { carregarResumo() }, [carregarResumo])
  useEffect(() => { if (novaAoAbrir && resumo?.link) setNova(true) }, [novaAoAbrir, resumo?.link])

  const irPara = (k, s) => {
    const n = new URLSearchParams(sp)
    n.set('aba', k)
    if (s) n.set('sub', s); else n.delete('sub')
    setSp(n)
  }
  const abrirNova = () => {
    if (resumo?.link) setNova(true)
    else errorModal(resumo?.motivo || 'Link de cotação indisponível para este licenciado.')
  }
  const fecharNova = () => {
    setNova(false)
    setRev((r) => r + 1)
    if (novaAoAbrir) nav('/registros/protecao-veicular?aba=cotacoes')
    carregarResumo()
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={24} className="text-accent-500" />
          <div>
            <h1 className="text-xl font-heading font-extrabold text-graphite">Proteção Veicular</h1>
            <p className="text-sm text-neutral-500">Cotações, contratos, vistorias e ganhos da operação oficial, direto no seu escritório</p>
          </div>
        </div>
        {isAdmin && <button onClick={() => nav('/registros/protecao-veicular/gestao')} className={btnSec}><Car size={15} /> Gestão (planos e integração)</button>}
      </div>

      {isAdmin && (
        <div className={`${card} p-4`}>
          <label className="block text-xs font-medium text-gray-600 mb-1">Licenciado</label>
          <SearchableSelect value={licId} onChange={(v) => { setLicId(v); const n = new URLSearchParams(sp); if (v) n.set('licenciado', v); else n.delete('licenciado'); setSp(n) }}
            placeholder="Buscar licenciado..." options={lics.map((l) => ({ value: l.id, label: l.nome }))} />
        </div>
      )}

      <div className="flex gap-1 overflow-x-auto overflow-y-hidden border-b border-neutral-200">
        {ABAS.map(([k, l, Icon]) => (
          <button key={k} onClick={() => irPara(k)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px ${aba === k ? 'border-accent-500 text-accent-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>

      {!p ? <p className="text-sm text-neutral-500">Selecione um licenciado para ver a Proteção Veicular dele.</p> : (
        <>
          {aba === 'inicio' && <Inicio params={p} irPara={irPara} abrirNova={abrirNova} resumo={resumo} recarregar={carregarResumo} />}
          {aba === 'cotacoes' && <Cotacoes key={rev} fresco={rev > 0} params={p} link={resumo?.link} abaInicial={sub} abrirNova={abrirNova} />}
          {aba === 'contratos' && <Contratos params={p} />}
          {aba === 'vistorias' && <Vistorias params={p} />}
          {aba === 'ganhos' && <Ganhos params={p} isAdmin={isAdmin} />}
        </>
      )}
      {nova && resumo?.link && <NovaCotacao link={resumo.link} onClose={fecharNova} />}
    </div>
  )
}
